import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectFileGroup } from 'api/project-files/types';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { useTranslate } from 'hooks/use-translation';

export interface UpdateFolderFormProps {
  folderName: string;
}

interface Props {
  folder: ProjectFileGroup;
  folders?: ProjectFileGroup[];
  onSubmit: (form: UpdateFolderFormProps, folderId: number) => void;
  onClose: VoidFunction;
  isUpdating: boolean;
}

const ModalTitle = styled.div`
  margin-bottom: var(--spacing-04);
`;

const SaveButton = styled(Button)`
  margin-left: var(--spacing-03);
`;

export const UpdateFolderModal = ({
  folder,
  folders,
  onSubmit,
  onClose,
  isUpdating,
}: Props) => {
  const translate = useTranslate();
  const folderNames = useMemo(
    () => folders?.map((_folder) => _folder.folderName.toLowerCase()) || [],
    [folders],
  );
  const [form] = Form.useForm<UpdateFolderFormProps>();
  const _onSubmit = async () => {
    const values = await form.validateFields();

    onSubmit(values, folder.folderId);
  };

  const initialValues = useMemo(() => {
    return {
      folderName: folder.folderName,
    };
  }, [folder.folderName]);

  return (
    <Modal
      visible
      onCancel={onClose}
      title={
        <ModalTitle>
          <FormattedMessage id="project.files.update.folder.modal.title" />
        </ModalTitle>
      }
      footer={
        <>
          <Button type="text" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>
          <SaveButton type="primary" onClick={_onSubmit} loading={isUpdating}>
            <FormattedMessage id="save" />
          </SaveButton>
        </>
      }
    >
      <Form
        layout="vertical"
        form={form}
        autoComplete="off"
        initialValues={initialValues}
      >
        <Form.Item
          required
          rules={[
            { required: true, message: translate('input.required') },
            ({ isFieldTouched }) => ({
              validator: (_, value) => {
                if (!isFieldTouched('folderName')) {
                  return Promise.resolve();
                }
                if (folderNames.includes(value?.toLowerCase())) {
                  return Promise.reject(new Error(`${value} already exists`));
                }

                return Promise.resolve();
              },
            }),
          ]}
          name="folderName"
          label={
            <FormattedMessage id="project.files.update.group.name.input.label" />
          }
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};
